import { useEffect, useMemo, useState } from 'react'
import type { PagaebleResult, ReporteAcumuladoComisionesDTO, SearchReciboDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke, { LONG_TIMEOUT_MS } from '../../../utils/APIInvoke'
import { formatMonthKey, monthIso, monthKeys, monthRangeIso, shiftMonthKey } from '../../../utils/dates'
import {
    ALL_ROWS,
    compactAmount,
    downloadCsv,
    errorMessage,
    formatAmount,
    formatSlashDate,
    matchesSearch,
    PAGE_SIZES,
    pageWindow,
    paginate,
    personName,
} from '../common/report-common'
import type { ReportColumn } from '../common/report-common'

const LOAD_ERROR = 'No se pudo cargar el reporte de comisiones.'
const RANGE_ERROR = 'El mes final debe ser igual o posterior al inicial.'

export const PRIMA_COLOR = '#4B44D8'
export const COMISION_COLOR = '#14A79F'
export const PREVIOUS_COLOR = '#B4B6C6'

export interface CommissionReceiptRow {
    id: number
    polizaId: number | null
    noPoliza: string
    serie: string
    cliente: string
    vendedor: string
    aseguradora: string
    total: number
    comision: number
    moneda: string
    vigenciaDe: string
    vigenciaA: string
    vence: string
    pago: string
    robot: boolean
}

export interface CommissionTotals {
    primas: number
    comisiones: number
    porcentaje: number
    anteriores: number
    diferencia: number
    diferenciaPct: number | null
}

const toRow = (item: SearchReciboDTO): CommissionReceiptRow => {
    const poliza = item.poliza
    return {
        id: item.id,
        polizaId: poliza?.id ?? null,
        noPoliza: String(poliza?.noPoliza || '').toUpperCase(),
        serie: item.serie != null ? `${item.serie}/${item.totalSerie ?? '—'}` : '—',
        cliente: poliza?.cliente ? personName(poliza.cliente) : '',
        vendedor: poliza?.vendedor ? personName(poliza.vendedor) : '',
        aseguradora: poliza?.agenteClave?.aseguradora?.abreviacion || '',
        total: Number(item.total) || 0,
        comision: Number(item.comision) || 0,
        moneda: poliza?.moneda?.abreviacion || 'MXN',
        vigenciaDe: formatSlashDate(item.fecVigDe) || '',
        vigenciaA: formatSlashDate(item.fecVigA) || '',
        vence: formatSlashDate(item.fecVence) || '',
        pago: formatSlashDate(item.fecPago) || '',
        robot: Boolean(poliza?.esRobot),
    }
}

export const CSV_COLUMNS: ReportColumn<CommissionReceiptRow>[] = [
    { key: 'noPoliza', label: 'No. póliza', csv: (row) => row.noPoliza },
    { key: 'serie', label: 'Serie', csv: (row) => row.serie },
    { key: 'cliente', label: 'Cliente', csv: (row) => row.cliente },
    { key: 'vendedor', label: 'Vendedor', csv: (row) => row.vendedor },
    { key: 'aseguradora', label: 'Aseguradora', csv: (row) => row.aseguradora },
    { key: 'total', label: 'Total', csv: (row) => row.total.toFixed(2) },
    { key: 'comision', label: 'Comisión', csv: (row) => row.comision.toFixed(2) },
    { key: 'moneda', label: 'Moneda', csv: (row) => row.moneda },
    { key: 'vigenciaDe', label: 'Vigencia de', csv: (row) => row.vigenciaDe },
    { key: 'vigenciaA', label: 'Vigencia a', csv: (row) => row.vigenciaA },
    { key: 'vence', label: 'Vencimiento', csv: (row) => row.vence },
    { key: 'pago', label: 'Fecha de pago', csv: (row) => row.pago },
    { key: 'robot', label: 'Robot', csv: (row) => (row.robot ? 'Sí' : 'No') },
]

const fetchAcumulados = async (from: string, to: string) => {
    const payload: WrapperResponse<ReporteAcumuladoComisionesDTO[]> | null = await (await APIInvoke.invokeGET(`/reportes/comisiones/acumulados?start=${from}&end=${to}`, { timeoutMs: LONG_TIMEOUT_MS })).json()
    if (!payload?.ok) throw new Error(payload?.message || LOAD_ERROR)
    return payload.body || []
}

const sum = (items: ReporteAcumuladoComisionesDTO[], key: 'prima' | 'comision') => items.reduce((acc, item) => acc + (Number(item[key]) || 0), 0)

export const useReportCommissionsModel = () => {
    const [startMonth, setStartMonth] = useState(() => monthIso(-11))
    const [endMonth, setEndMonth] = useState(() => monthIso(0))
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [current, setCurrent] = useState<ReporteAcumuladoComisionesDTO[]>([])
    const [previous, setPrevious] = useState<ReporteAcumuladoComisionesDTO[]>([])
    const [recibos, setRecibos] = useState<SearchReciboDTO[]>([])
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[1])
    const [detailId, setDetailId] = useState<number | null>(null)

    const rangeInvalid = Boolean(startMonth && endMonth && startMonth > endMonth)

    useEffect(() => {
        if (!startMonth || !endMonth || rangeInvalid) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const range = monthRangeIso(startMonth, endMonth)
                const previousRange = monthRangeIso(startMonth, endMonth, -1)
                const [currentItems, previousItems, recibosPayload] = await Promise.all([
                    fetchAcumulados(range.from, range.to),
                    fetchAcumulados(previousRange.from, previousRange.to),
                    APIInvoke.invokeGET(`/recibos?fecPago[bw]=${range.from},${range.to}&status=PAGADO&page=1&size=${ALL_ROWS}&order=fecPago%20asc`, { timeoutMs: LONG_TIMEOUT_MS })
                        .then((response) => response.json() as Promise<WrapperResponse<PagaebleResult<SearchReciboDTO>>>),
                ])
                if (!active) return
                setCurrent(currentItems)
                setPrevious(previousItems)
                setRecibos(recibosPayload?.ok ? recibosPayload.body?.items || [] : [])
                setPage(1)
            } catch (loadError) {
                if (active) {
                    setCurrent([])
                    setPrevious([])
                    setRecibos([])
                    setError(errorMessage(loadError, LOAD_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [startMonth, endMonth, rangeInvalid, reloadToken])

    const totals = useMemo<CommissionTotals>(() => {
        const primas = sum(current, 'prima')
        const comisiones = sum(current, 'comision')
        const anteriores = sum(previous, 'comision')
        const diferencia = comisiones - anteriores
        return {
            primas,
            comisiones,
            porcentaje: primas === 0 ? 0 : (comisiones * 100) / primas,
            anteriores,
            diferencia,
            diferenciaPct: anteriores === 0 ? null : (diferencia / Math.abs(anteriores)) * 100,
        }
    }, [current, previous])

    const months = useMemo(() => monthKeys(startMonth, endMonth), [startMonth, endMonth])

    const primaVsComision = useMemo(() => {
        const byKey = new Map(current.map((item) => [item.fecha, item]))
        return months.map((key) => ({
            key,
            label: formatMonthKey(key),
            values: [Number(byKey.get(key)?.prima) || 0, Number(byKey.get(key)?.comision) || 0],
        }))
    }, [current, months])

    const vsAnterior = useMemo(() => {
        const currentByKey = new Map(current.map((item) => [item.fecha, item]))
        const previousByKey = new Map(previous.map((item) => [item.fecha, item]))
        return months.map((key) => ({
            key,
            label: formatMonthKey(key),
            values: [Number(currentByKey.get(key)?.comision) || 0, Number(previousByKey.get(shiftMonthKey(key, -1))?.comision) || 0],
        }))
    }, [current, previous, months])

    const allRows = useMemo(() => recibos.map(toRow), [recibos])
    const filteredRows = useMemo(
        () => allRows.filter((row) => matchesSearch(search, row.noPoliza, row.cliente, row.vendedor, row.aseguradora)),
        [allRows, search],
    )
    const paged = paginate(filteredRows, page, pageSize)

    return {
        startMonth,
        endMonth,
        rangeInvalid,
        rangeError: rangeInvalid ? RANGE_ERROR : null,
        loading,
        error,
        totals,
        primaVsComision,
        vsAnterior,
        search,
        rows: paged.rows,
        filteredCount: filteredRows.length,
        page: paged.page,
        totalPages: paged.totalPages,
        pages: filteredRows.length > 0 ? pageWindow(paged.page, paged.totalPages) : [],
        pageSize,
        rangeFrom: paged.rangeFrom,
        rangeTo: paged.rangeTo,
        detailId,
        formatMoney: (value: number) => `$${formatAmount(value)}`,
        formatAxis: compactAmount,
        changeStartMonth: setStartMonth,
        changeEndMonth: setEndMonth,
        changeSearch: (value: string) => {
            setSearch(value)
            setPage(1)
        },
        setPage,
        changePageSize: (value: number) => {
            setPageSize(value)
            setPage(1)
        },
        exportCsv: () => downloadCsv('reporte-comisiones', CSV_COLUMNS, filteredRows),
        reload: () => setReloadToken((token) => token + 1),
        openPoliza: (id: number | null) => { if (id) setDetailId(id) },
        closePoliza: () => setDetailId(null),
    }
}
